/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import PropTypes from 'prop-types';
import React__default, { useRef, useState, useLayoutEffect } from 'react';
import cx from 'classnames';
import { useId } from '../../internal/useId.js';
import { usePrefix } from '../../internal/usePrefix.js';
import Tag, { SIZES, TYPES } from './Tag.js';
import { Close } from '@carbon/icons-react';
import '../Tooltip/DefinitionTooltip.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import '../Text/index.js';
import { isEllipsisActive } from './isEllipsisActive.js';
import { Text } from '../Text/Text.js';

var _Close;
const DismissibleTag = _ref => {
  let {
    className,
    disabled,
    id,
    renderIcon,
    title = 'Dismiss',
    onClose,
    slug,
    size,
    text,
    tagTitle,
    type,
    ...other
  } = _ref;
  const prefix = usePrefix();
  const tagLabelRef = useRef();
  const tagId = id || `tag-${useId()}`;
  const tagClasses = cx(`${prefix}--tag--filter`, className);
  const [isEllipsisApplied, setIsEllipsisApplied] = useState(false);
  useLayoutEffect(() => {
    const newElement = tagLabelRef.current?.getElementsByClassName(`${prefix}--tag__label`)[0];
    setIsEllipsisApplied(isEllipsisActive(newElement));
  }, [prefix, tagLabelRef]);
  const handleClose = event => {
    if (onClose) {
      event.stopPropagation();
      onClose(event);
    }
  };
  let normalizedSlug;
  if (slug && slug['type']?.displayName === 'AILabel') {
    normalizedSlug = /*#__PURE__*/React__default.cloneElement(slug, {
      size: 'sm',
      kind: 'inline'
    });
  }
  const tooltipClasses = cx(`${prefix}--icon-tooltip`, `${prefix}--tag-label-tooltip`);

  // Removing onClick from the spread operator
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    onClick,
    ...otherProps
  } = other;
  const dismissLabel = `Dismiss "${text}"`;
  return /*#__PURE__*/React__default.createElement(Tag, _extends({
    ref: tagLabelRef,
    type: type,
    size: size,
    renderIcon: renderIcon,
    disabled: disabled,
    className: tagClasses,
    id: tagId
  }, otherProps), /*#__PURE__*/React__default.createElement("div", {
    className: `${prefix}--interactive--tag-children`
  }, /*#__PURE__*/React__default.createElement(Text, {
    title: tagTitle ? tagTitle : text,
    className: `${prefix}--tag__label`
  }, text), normalizedSlug, /*#__PURE__*/React__default.createElement(Tooltip, {
    label: isEllipsisApplied ? dismissLabel : title,
    align: "bottom",
    className: tooltipClasses,
    leaveDelayMs: 0,
    closeOnActivation: true
  }, /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    className: `${prefix}--tag__close-icon`,
    onClick: handleClose,
    disabled: disabled,
    "aria-label": title
  }, _Close || (_Close = /*#__PURE__*/React__default.createElement(Close, null))))));
};
DismissibleTag.propTypes = {
  /**
   * Provide a custom className that is applied to the containing <span>
   */
  className: PropTypes.string,
  /**
   * Specify if the `DismissibleTag` is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Specify the id for the tag.
   */
  id: PropTypes.string,
  /**
   * Click handler for filter tag close button.
   */
  onClose: PropTypes.func,
  /**
   * Optional prop to render a custom icon.
   * Can be a React component class
   */
  renderIcon: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  /**
   * Specify the size of the Tag. Currently supports either `sm`,
   * `md` (default) or `lg` sizes.
   */
  size: PropTypes.oneOf(Object.keys(SIZES)),
  /**
   * **Experimental:** Provide a `Slug` component to be rendered inside the `DismissibleTag` component
   */
  slug: PropTypes.node,
  /**
   * Provide text to be rendered inside of a the tag.
   */
  text: PropTypes.string,
  /**
   * Provide a custom `title` to be inserted in the tag.
   */
  tagTitle: PropTypes.string,
  /**
   * Text to show on clear filters
   */
  title: PropTypes.string,
  /**
   * Specify the type of the `Tag`
   */
  type: PropTypes.oneOf(Object.keys(TYPES))
};

export { DismissibleTag as default };
